import { useCallback, useEffect, useState } from "react";
import { Widget } from "@/modules";
import { useEditWidget } from "../context/EditWidgetContext";
import { widgetEndpoints } from "@/modules/widgets/api/endpoints";
import { transformDraftToWidgetPayload } from "@/modules/widgets/utils/widgetTransform";
import { useDashboard } from "@/modules/dashboard/context/DashboardContext";
import { usePage } from "@/modules/pages/context/PageContext";
import { useWidget } from "../context/WidgetContext";

interface UseEditWidgetDrawerParams {
    onWidgetUpdated?: (widget: Widget) => void;
    dashboardDate: { from: Date; to: Date };
}

export const useEditWidgetDrawer = ({ onWidgetUpdated, dashboardDate }: UseEditWidgetDrawerParams) => {
    const { isOpen, widget, closeEditDrawer } = useEditWidget();
    const [isUpdatingWidget, setIsUpdatingWidget] = useState(false);
    const { state: dashboardState } = useDashboard();
    const { state: pageState } = usePage();
    const { dispatch } = useWidget();

    const handleUpdateManualWidget = useCallback(
        async (draft: any, hierarchyData?: { levels: any[]; segments: any[]; metrics: any[]; products: any[] }) => {
            if (!widget || !dashboardState.currentDashboard || !pageState.currentPage) {
                console.error("Widget, dashboard, or page not available");
                return;
            }

            setIsUpdatingWidget(true);
            try {
                // Transform draft to widget payload
                const payload = transformDraftToWidgetPayload(
                    draft,
                    pageState.currentPage.id,
                    pageState.currentTab || null,
                    hierarchyData?.levels || [],
                    hierarchyData?.segments || [],
                    hierarchyData?.metrics || [],
                    hierarchyData?.products || [],
                    dashboardState.currentDashboard.clientId,
                    { pageDateRange: dashboardDate }
                );

                // Merge with existing widget to preserve id and other fields
                const updatedWidget: Widget = {
                    ...widget,
                    ...(payload as any),
                    updatedAt: new Date().toISOString(),
                };

                // Call API to update widget
                const response = await widgetEndpoints.updateWidget(updatedWidget);

                if (response.widget) {
                    dispatch({ type: "UPDATE_WIDGET", payload: response.widget });
                    if (onWidgetUpdated) {
                        onWidgetUpdated(response.widget);
                    }
                }

                setIsUpdatingWidget(false);
                closeEditDrawer();
            } catch (err) {
                setIsUpdatingWidget(false);
                console.error("Failed to update widget:", err);
            }
        },
        [widget, dashboardState.currentDashboard, pageState.currentPage, pageState.currentTab, onWidgetUpdated, closeEditDrawer, dispatch]
    );

    const handleCancel = useCallback(() => {
        closeEditDrawer();
    }, [closeEditDrawer]);

    return {
        isOpen,
        widget,
        isUpdatingWidget,
        handleUpdateManualWidget,
        handleCancel,
        closeEditDrawer,
    };
};
